// src/stores/paymentStore.js
import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import svc from "@/services/finance/payments.service";
import * as Toast from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};


export const usePaymentStore = defineStore("payments", () => {
  const items = ref([]);
  const loading = ref(false);
  const meta = ref({ total: 0, per_page: 25, current_page: 1 });

  const summary = ref({
    count: 0,
    total_amount: 0,
  });

  const filters = reactive({
    from: null,
    to: null,
    q: "",
    method: "",
    sort: "recent",
  });

  const isEmpty = computed(() => !items.value.length && !loading.value);

  async function fetchAll(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      items.value = [];
      summary.value = { count: 0, total_amount: 0 };
      return;
    }

    loading.value = true;
    try {
      const res = await svc.list({
        page: params.page ?? meta.value.current_page ?? 1,
        per_page: params.per_page ?? meta.value.per_page ?? 25,
        from: params.from ?? filters.from ?? undefined,
        to: params.to ?? filters.to ?? undefined,
        q: params.q ?? filters.q ?? undefined,
        method: params.method ?? filters.method ?? undefined,
        sort: params.sort ?? filters.sort ?? undefined,
      });

      const data = res.data;

      items.value = data.data || data;
      meta.value = {
        total: data.total,
        per_page: data.per_page,
        current_page: data.current_page,
      };
      summary.value = data.summary || { count: 0, total_amount: 0 };
    } catch (e) {
      notifyError("Failed to load payments");
    } finally {
      loading.value = false;
    }
  }

  async function exportPdf() {
    try {
      const res = await svc.exportPdf({
        from: filters.from || undefined,
        to: filters.to || undefined,
        q: filters.q || undefined,
        method: filters.method || undefined,
        sort: filters.sort || undefined,
      });
      return res.data; 
    } catch (e) {
      notifyError("Failed to export PDF");
      throw e;
    }
  }

  async function exportExcel() {
    try {
      const res = await svc.exportExcel({
        from: filters.from || undefined,
        to: filters.to || undefined,
        q: filters.q || undefined,
        method: filters.method || undefined,
        sort: filters.sort || undefined,
      });
      return res.data; // blob
    } catch (e) {
      notifyError("Failed to export Excel");
      throw e;
    }
  }

  return {
    items,
    loading,
    meta,
    summary,
    filters,
    isEmpty,
    fetchAll,
    exportPdf,
    exportExcel,
  };
});
